import { api } from '@/util/api'
import { safeReturnTo } from '@/components/onboarding/authContinuation'
import { apiFailure } from '@/lib/apiFailure'
import type {
  AcceptFailure,
  AcceptedInvitation,
  GoneInvitationState,
  InvitationEvent,
  InvitationInviter,
  InvitationPreview,
  InvitationProfile,
  MyInvitation,
  PreviewFailure,
} from './types'

/**
 * The invite page's two calls (docs/plans/2026-10-19-watch-invites.md, "API contract"), and the signed-in account's
 * waiting manager invitations (docs/features/event-managers.md). Calls with a token take it in the body, never the path
 * or query string, which servers log. The shared client adds the session token and resolves to the response body: read
 * it with `readInvitationPreview`, `readAcceptedInvitation` and `readMyInvitations`.
 */
export const invitesApi = {
  /** Anyone with the link, signed in or not. */
  preview: (token: string) => api.post<unknown>('/public/invitations/preview', { token }),
  /** Signed in. Taking your own host invitation again answers with the stage the first time made. */
  accept: (token: string) => api.post<unknown>('/invitations/accept', { token }),
  /** Signed in: manager invitations for this account (its confirmed email address, or the account itself) not yet
   *  taken. */
  mine: () => api.get<unknown>('/invitations/mine'),
  /** Signed in: takes one of `mine` by its id, answering as `accept` does. */
  acceptById: (invitationId: string) => api.post<unknown>(`/invitations/${encodeURIComponent(invitationId)}/accept`),
}

const record = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null
const text = (value: unknown): value is string => typeof value === 'string'
const KINDS = ['host', 'guest', 'manager'] as const
const MANAGER_BINDINGS = ['email', 'account', 'link'] as const
const STATES = ['open', 'expired', 'used', 'revoked'] as const
const GONE_STATES: readonly string[] = ['expired', 'used', 'revoked']
/** The 409 types accepting answers with, each its own failure. */
const CONFLICTS = ['already_host', 'stage_full', 'chat_blocked', 'already_on_stage'] as const
/** An app path, as a sign-in's continuation must be (`safeReturnTo`: no other site, backslash or control character). */
const appPath = (value: unknown): value is string => text(value) && value !== '' && safeReturnTo(value, '') === value
const oneOf = <T extends string>(options: readonly T[], value: unknown): value is T => options.includes(value as T)

function readProfile(value: unknown): InvitationProfile | null {
  if (!record(value) || !text(value.handle) || !text(value.name)) return null
  return { handle: value.handle, name: value.name, avatar_url: text(value.avatar_url) ? value.avatar_url : null }
}

function readEvent(value: unknown): InvitationEvent | null {
  if (!record(value) || !text(value.id) || !text(value.name) || !text(value.shortcode)) return null
  return {
    id: value.id,
    name: value.name,
    shortcode: value.shortcode,
    starts_at: text(value.starts_at) ? value.starts_at : null,
    watch_screen_enabled: value.watch_screen_enabled === true,
  }
}

function readInviter(value: unknown): InvitationInviter | null {
  if (!record(value)) return null
  const named = (part: unknown) => (text(part) && part.trim() !== '' ? part : null)
  return { name: named(value.name), organization_name: named(value.organization_name) }
}

/** The preview answer's `data`, or null when the body isn't one (a page in place of JSON, say). A manager invitation
 *  names the owner organization (its `organization_name`, its inviter's, or its `stage_host`'s) and may come without a
 *  `stage_host`, which is then made from that name. */
export function readInvitationPreview(body: unknown): InvitationPreview | null {
  const data = record(body) ? body.data : null
  if (!record(data) || !oneOf(KINDS, data.kind) || !oneOf(STATES, data.state)) return null
  const event = readEvent(data.event)
  if (!event) return null
  const base = { kind: data.kind, state: data.state, expires_at: text(data.expires_at) ? data.expires_at : '', event }
  const stageHost = readProfile(data.stage_host)
  if (data.kind !== 'manager') return stageHost && { ...base, stage_host: stageHost }

  const inviter = readInviter(data.inviter)
  const organization = [data.organization_name, inviter?.organization_name, stageHost?.name].find(
    (name): name is string => text(name) && name.trim() !== '',
  )
  if (!organization) return null
  const binding = MANAGER_BINDINGS.find((option) => option === data.binding)
  return {
    ...base,
    stage_host: stageHost ?? { handle: '', name: organization, avatar_url: null },
    organization_name: organization,
    ...(inviter ? { inviter } : {}),
    ...(binding ? { binding } : {}),
    ...(text(data.email_hint) && data.email_hint ? { email_hint: data.email_hint } : {}),
    ...(text(data.handle_hint) && data.handle_hint ? { handle_hint: data.handle_hint } : {}),
  }
}

/** The accept answer's `data`, or null when the body isn't one. Its path must be the app's own. A stage's answer
 *  carries its `stage_id`; a manager invitation's (`kind: "manager"`, or no kind and no stage, as accepting one of
 *  `mine` may answer with just its `path`) leads to the event's workspace. */
export function readAcceptedInvitation(body: unknown): AcceptedInvitation | null {
  const data = record(body) ? (record(body.data) ? body.data : body) : null
  if (!record(data) || !appPath(data.path)) return null
  if (data.kind === 'manager' || (data.kind === undefined && data.stage_id === undefined)) {
    return { kind: 'manager', path: data.path }
  }
  if (!oneOf(KINDS, data.kind) || !text(data.stage_id)) return null
  return { kind: data.kind, stage_id: data.stage_id, path: data.path }
}

/** The `mine` answer's invitations, skipping any it can't read; null when the body isn't a list. */
export function readMyInvitations(body: unknown): MyInvitation[] | null {
  const data = record(body) ? body.data : null
  if (!Array.isArray(data)) return null
  return data.flatMap((item: unknown): MyInvitation[] => {
    if (!record(item) || !text(item.id) || (item.kind !== undefined && item.kind !== 'manager')) return []
    const event = readEvent(item.event)
    const inviter = readInviter(item.inviter)
    if (!event || !inviter) return []
    return [{
      id: item.id,
      kind: 'manager',
      binding: item.binding === 'account' ? 'account' : 'email',
      event: { id: event.id, name: event.name, shortcode: event.shortcode, starts_at: event.starts_at },
      inviter,
      expires_at: text(item.expires_at) ? item.expires_at : '',
    }]
  })
}

/** What an error body says beside its type: `error.<key>`, or the body's own `<key>` or `data.<key>`. */
function errorDetail(error: unknown, key: string): unknown {
  const body = (error as { response?: { data?: unknown } } | null)?.response?.data
  if (!record(body)) return undefined
  if (record(body.error) && body.error[key] !== undefined) return body.error[key]
  if (body[key] !== undefined) return body[key]
  return record(body.data) ? body.data[key] : undefined
}

/** Why a rejected preview call failed. */
export function previewFailure(error: unknown): PreviewFailure {
  const { status, retryAfterMs } = apiFailure(error)
  if (status === 404) return { kind: 'not_found' }
  if (status === 429) return { kind: 'throttled', retryAfterMs }
  return { kind: 'failed', offline: status === null }
}

/** Why a rejected accept call failed, by its status and the API's error type. */
export function acceptFailure(error: unknown): AcceptFailure {
  const failure = apiFailure(error)
  const state = (error as { response?: { data?: { error?: { state?: unknown } } } } | null)?.response?.data?.error?.state
  switch (failure.status) {
    case 410:
      return { kind: 'gone', state: oneOf(GONE_STATES, state) ? (state as GoneInvitationState) : 'used' }
    case 404:
      return { kind: 'not_found' }
    case 409: {
      if (failure.type === 'already_manager') {
        const path = errorDetail(error, 'path')
        return { kind: 'already_manager', path: appPath(path) ? path : null }
      }
      return oneOf(CONFLICTS, failure.type) ? { kind: failure.type } : { kind: 'failed', offline: false }
    }
    case 422:
      return failure.type === 'username_required' ? { kind: 'username_required' } : { kind: 'failed', offline: false }
    case 401:
      return { kind: 'signed_out' }
    case 403: {
      if (failure.type !== 'wrong_account' && failure.type !== 'email_unconfirmed') return { kind: 'refused' }
      const detail = (key: string) => {
        const hint = errorDetail(error, key)
        return text(hint) && hint ? hint : null
      }
      if (failure.type === 'email_unconfirmed') return { kind: 'email_unconfirmed', emailHint: detail('email_hint') }
      return { kind: 'wrong_account', emailHint: detail('email_hint'), handleHint: detail('handle_hint') }
    }
    case 429:
      return { kind: 'throttled', retryAfterMs: failure.retryAfterMs }
    default:
      return { kind: 'failed', offline: failure.status === null }
  }
}

/**
 * A stage on the event's watch screen, as the API's `Stage.path_for` makes it: the owner organization's at
 * `/e/<shortcode>`, a person's at `/e/<shortcode>/@<handle>`.
 */
export function stagePath(event: Pick<InvitationEvent, 'shortcode'>, handle?: string | null): string {
  let path = `/e/${encodeURIComponent(event.shortcode)}`
  if (handle) path += `/@${encodeURIComponent(handle)}`
  return path
}
